import { inject, Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { LocalStorageService } from './localStorage.service';
import { isWebViewUserAgent } from '../helpers';

const GATE_KEY = 'sw-download-gate';

/**
 * Gates Angular service-worker registration on the offline-route feature.
 *
 * The service worker only serves the offline hiking-route feature, so it is not
 * registered for visitors who never download a route. Registration is triggered
 * the first time a download starts (`triggerRegistration`) and the decision is
 * persisted so it sticks on later visits. An existing registration (from a prior
 * build) is treated as gate-satisfied so an offline-capable user is never
 * un-registered.
 */
@Injectable({ providedIn: 'root' })
export class SwRegistrationService {
  private localStorage = inject(LocalStorageService);

  private readonly trigger = new ReplaySubject<void>(1);
  /** Emits once when the service worker should register. */
  readonly trigger$: Observable<void> = this.trigger.asObservable();
  private fired = false;

  constructor() {
    // Offline routes (and their service worker) are a WebView-only feature.
    // Remove registrations left by builds that enabled Angular SW in browsers.
    if (!isWebViewUserAgent()) {
      void this.cleanupStaleBrowserWorker();
      return;
    }

    if (this.localStorage.getItem<boolean>(GATE_KEY)) {
      this.fire();
      return;
    }

    if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
      navigator.serviceWorker
        .getRegistration()
        .then((registration) => {
          if (registration) this.triggerRegistration();
        })
        .catch(() => {});
    }
  }

  /** Called at the start of a route download to allow SW registration (WebView only). */
  triggerRegistration(): void {
    if (this.fired || !isWebViewUserAgent()) return;
    this.localStorage.setItem(GATE_KEY, true);
    this.fire();
  }

  private fire(): void {
    if (this.fired) return;
    this.fired = true;
    this.trigger.next();
  }

  private async cleanupStaleBrowserWorker(): Promise<void> {
    this.localStorage.removeItem(GATE_KEY);
    await Promise.all([
      this.unregisterAngularWorkers(),
      this.deleteAngularCaches(),
    ]);
  }

  private async unregisterAngularWorkers(): Promise<void> {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker)
      return;

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      const angularWorkers = registrations.filter(registration =>
        this.isAngularWorker(registration)
      );
      await Promise.allSettled(angularWorkers.map(registration => registration.unregister()));
    } catch {
      // Cleanup is best-effort and must never block application startup.
    }
  }

  private isAngularWorker(registration: ServiceWorkerRegistration): boolean {
    const worker = registration.active ?? registration.waiting ?? registration.installing;
    return worker?.scriptURL.endsWith('/ngsw-worker.js') ?? false;
  }

  private async deleteAngularCaches(): Promise<void> {
    if (typeof caches === 'undefined')
      return;

    try {
      const keys = await caches.keys();
      await Promise.allSettled(
        keys.filter(key => key.startsWith('ngsw:')).map(key => caches.delete(key))
      );
    } catch {
      // Cleanup is best-effort and unsupported storage must not break the PWA.
    }
  }
}
